import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "./api";

/**
 * Is this browser signed in to a dashboard? Asked once per page load and remembered. The answer
 * comes from the server because the session cookie is HttpOnly. Anything that signs in or out
 * calls refreshSignedIn() so the next answer is fresh.
 */
let answer: Promise<boolean> | null = null;
const listeners = new Set<(signedIn: boolean) => void>();

function ask(): Promise<boolean> {
  if (!answer) {
    answer = api.get<{ signedIn: boolean }>("/api/auth/session").then((res) => res.ok && res.data.signedIn === true);
  }
  return answer;
}

/** Forget the remembered answer and ask again, telling anything on screen. */
export async function refreshSignedIn(): Promise<void> {
  answer = null;
  const signedIn = await ask();
  listeners.forEach((l) => l(signedIn));
}

/** False until the server says yes, so a signed-out visitor never sees a
 *  signed-in page flash up. */
export function useSignedIn(): boolean {
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    let live = true;
    ask().then((v) => live && setSignedIn(v));
    listeners.add(setSignedIn);
    return () => {
      live = false;
      listeners.delete(setSignedIn);
    };
  }, []);
  return signedIn;
}

/** Where the logo goes: the dashboard for someone signed in, the home page for
 *  everyone else. */
export function useHomePath(): string {
  return useSignedIn() ? "/dashboard" : "/";
}

/** "Sign in" for a visitor, a link to the dashboard for an org that is signed in. */
export function AccountLink({
  children,
  signedInLabel = "Dashboard",
  className,
  style,
}: {
  children: React.ReactNode;
  signedInLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const signedIn = useSignedIn();
  return (
    <Link to={signedIn ? "/dashboard" : "/login"} className={className} style={style}>
      {signedIn ? signedInLabel : children}
    </Link>
  );
}
